/* eslint-disable @typescript-eslint/no-explicit-any */
import { fetchWithHeaders } from '@sendible/common';
import sharedStateBridge from '@sendible/shared-state-bridge';
import {
  getCalendarCardMedias,
  hasCalendarEndpointQueryKey,
  keyFromDate,
  startOfWeek,
  processTags,
  renderLinksToHtml,
  getFailureReasonMessage,
} from './utils';
import { DAILY_VIEW_DATE_RANGE_DAYS } from './components/CalendarGrid/components/DailyGrid/hooks';
import endpoints from '../../data-layer/endpoints';
import queryClient from '../../data-layer/queryClient';

export type ExportCredentials = {
  accessToken: string;
  userId: number | string;
  email: string;
  isSwitchedUser?: boolean;
  username?: string;
};

type SummaryBucket = {
  date: string;
  messageCount: number;
  messages: CalendarMessage[];
};

export type ExportStatusFilter = 'scheduled' | 'sent' | 'failed';

const SCHEDULED_STATUSES = new Set(['Draft', 'Pending', 'ForApproval', 'Scheduled', 'Queued']);
const SENT_STATUSES = new Set(['Sent', 'Bounced']);

const { GetCalendarSummary, GetCalendar } = endpoints;

function collectCalendarSummaryBuckets(): SummaryBucket[] {
  const queries = queryClient.getQueryCache().findAll({
    predicate: ({ queryKey }) => hasCalendarEndpointQueryKey(queryKey, GetCalendarSummary.endpoint),
  });
  const [lastQuery] = [...queries].reverse();
  const all: SummaryBucket[] = [];

  const data = queryClient.getQueryData<SummaryBucket[] | undefined>(lastQuery.queryKey);

  if (Array.isArray(data)) all.push(...data);

  const byDate: Record<string, SummaryBucket> = {};

  all.forEach((b) => {
    const existing = byDate[b.date];

    if (!existing) {
      byDate[b.date] = { ...b };
    } else {
      const ids = new Set(existing.messages.map((m) => m.id));

      b.messages.forEach((m) => {
        if (!ids.has(m.id)) existing.messages.push(m);
      });
      existing.messageCount = existing.messages.length;
    }
  });

  return Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : 1));
}

export function getMonthSummaryBuckets(): SummaryBucket[] {
  const all = collectCalendarSummaryBuckets();

  return all;
}

export function getWeekSummaryBuckets(anchorISO: CalendarMonthString, weekStart: UserWeekStartDay): SummaryBucket[] {
  const weekStartDate = startOfWeek(anchorISO, weekStart);
  const days: Set<string> = new Set();

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStartDate);

    d.setDate(weekStartDate.getDate() + i);
    days.add(keyFromDate(d));
  }

  const all = collectCalendarSummaryBuckets();

  return all;
}

function escapeCsv(value: string | number | null | undefined): string {
  if (value == null) return '';

  const s = String(value);
  const needsQuotes = /[",\n]/.test(s);
  const v = s.replace(/"/g, '""');

  return needsQuotes ? `"${v}"` : v;
}

function extractMessage(m: CalendarMessage): string {
  return m.body?.messageText || '';
}

function extractSubject(m: CalendarMessage): string {
  return m.subject || '';
}

function extractImages(m: CalendarMessage): string {
  const mediaArr: any[] = Array.isArray(m.body?.media) ? m.body!.media : [];
  const urls: string[] = [];

  mediaArr.forEach((it) => {
    if (it?.thumbnailUrl) urls.push(it.thumbnailUrl);
  });

  return [...new Set(urls)].join(',');
}

function extractMediaUrls(m: CalendarMessage): string {
  const mediaArr: any[] = Array.isArray(m.body?.media) ? m.body!.media : [];
  const urls: string[] = [];

  mediaArr.forEach((it) => {
    if (it?.url) urls.push(it.url);
  });

  return [...new Set(urls)].join(',');
}

function extractTags(m: CalendarMessage): string {
  const text = extractMessage(m);
  const hashTagRegex = /#[\w\u00C0-\u017F]+/g;
  const tags = text.match(hashTagRegex) ?? [];

  return [...new Set(tags)].join(',');
}

function extractCampaigns(m: CalendarMessage): string {
  const campaigns: any[] = Array.isArray((m as any).campaigns) ? (m as any).campaigns : [];

  return campaigns
    .map((c: any) => c?.name ?? String(c))
    .filter(Boolean)
    .join(',');
}

function extractSendDate(m: CalendarMessage): string {
  return m.sendDateLocalized || m.sendDate || '';
}

function buildRows(messages: CalendarMessage[]): string[] {
  return messages.map((m) =>
    [
      processTags(extractMessage(m).replace(/\r?\n/g, ' ')),
      extractSubject(m).replace(/\r?\n/g, ' '),
      extractMediaUrls(m),
      extractImages(m),
      extractSendDate(m),
      extractTags(m),
      extractCampaigns(m),
    ]
      .map(escapeCsv)
      .join(',')
  );
}

function downloadCsv(rows: string[], filename: string) {
  const header = 'Message,Subject,URL,Image,SendDate,Tags,Campaigns';
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportWeekSummaryCsv(anchorISO: CalendarMonthString, weekStart: UserWeekStartDay) {
  const buckets = getWeekSummaryBuckets(anchorISO, weekStart);
  const weekStartDate = startOfWeek(anchorISO, weekStart);
  const weekEndDate = new Date(weekStartDate);

  weekEndDate.setDate(weekStartDate.getDate() + 6);

  const startStr = keyFromDate(weekStartDate);
  const endStr = keyFromDate(weekEndDate);
  const filename = `calendar-${startStr}-to-${endStr}.csv`;

  const messages = buckets.flatMap((b) => b.messages);
  const sorted = messages.sort((a, b) => extractSendDate(a).localeCompare(extractSendDate(b)));

  downloadCsv(buildRows(sorted), filename);
}

export function exportMonthlySummaryCsv(anchorISO: CalendarMonthString) {
  const buckets = getMonthSummaryBuckets();
  const filename = `calendar-${anchorISO.slice(0, 7)}.csv`;

  const messages = buckets.flatMap((b) => b.messages);
  const sorted = messages.sort((a, b) => extractSendDate(a).localeCompare(extractSendDate(b)));

  downloadCsv(buildRows(sorted), filename);
}

// Params the calendar FilterBar can set on calendar queries (see FilterBar API_PARAM_KEY_MAP + statuses).
const CALENDAR_FILTER_PARAM_KEYS = ['userIds', 'profileIds', 'profileListIds', 'queueIds', 'campaignIds', 'statuses'];

export function queryKeyMatchesFilterParams(queryKey: unknown, filterParams: Record<string, string> = {}): boolean {
  const [, params] = (Array.isArray(queryKey) ? queryKey : []) as [unknown, Record<string, unknown> | undefined];
  const keyParams = params && typeof params === 'object' ? params : {};
  const keysToCompare = new Set([...CALENDAR_FILTER_PARAM_KEYS, ...Object.keys(filterParams)]);

  return [...keysToCompare].every((key) => String(keyParams[key] ?? '') === String(filterParams[key] ?? ''));
}

function collectCachedDailyMessages(filterParams: Record<string, string> = {}): Record<number, CalendarMessage> {
  const queries = queryClient.getQueryCache().findAll({
    predicate: ({ queryKey }) => hasCalendarEndpointQueryKey(queryKey, GetCalendar.endpoint) && queryKeyMatchesFilterParams(queryKey, filterParams),
  });
  const allMessages: Record<number, CalendarMessage> = {};

  queries.forEach((q) => {
    const data = queryClient.getQueryData<any>(q.queryKey);
    const pages = data?.pages;

    if (!Array.isArray(pages)) return;
    pages.forEach((page: any) => {
      if (Array.isArray(page)) {
        page.forEach((m: CalendarMessage) => {
          if (m && m.id != null) allMessages[m.id] = m;
        });
      } else if (Array.isArray(page?.messages)) {
        page.messages.forEach((m: CalendarMessage) => {
          if (m && m.id != null) allMessages[m.id] = m;
        });
      } else if (Array.isArray(page?.days)) {
        page.days.forEach((d: CalendarData) => {
          d.messages.forEach((m: CalendarMessage) => {
            if (m && m.id != null) allMessages[m.id] = m;
          });
        });
      }
    });
  });

  return allMessages;
}

export function exportRangeMessagesCsv(anchorISO: CalendarMonthString, filterParams: Record<string, string> = {}) {
  const anchorDate = new Date(anchorISO);
  const startDate = new Date(anchorDate);

  startDate.setDate(anchorDate.getDate() - DAILY_VIEW_DATE_RANGE_DAYS);

  const endDate = new Date(anchorDate);

  endDate.setDate(anchorDate.getDate() + DAILY_VIEW_DATE_RANGE_DAYS);

  const startStr = keyFromDate(startDate);
  const endStr = keyFromDate(endDate);
  const filename = `calendar-${startStr}-to-${endStr}.csv`;

  const allMessages = collectCachedDailyMessages(filterParams);
  const messages = Object.values(allMessages).sort((a, b) => extractSendDate(a).localeCompare(extractSendDate(b)));

  downloadCsv(buildRows(messages), filename);
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

async function fetchPagedMessages(baseParams: Record<string, unknown>): Promise<Record<number, CalendarMessage>> {
  const perPage = 100;
  const collected: Record<number, CalendarMessage> = {};
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetchWithHeaders({
      method: 'GET',
      url: endpoints.GetCalendar.endpoint,
      params: { ...baseParams, page, pageSize: perPage, perPage, includeDeliveryRecipients: true },
    });
    const payload = response as { result?: CalendarMessage[] } | CalendarMessage[];
    const result: CalendarMessage[] = Array.isArray(payload) ? payload : (payload.result ?? []);

    if (!Array.isArray(result) || result.length === 0) {
      hasMore = false;
    } else {
      result.forEach((m) => {
        if (m?.id != null) collected[m.id] = m;
      });
      hasMore = result.length >= perPage;
      page += 1;
    }
  }

  return collected;
}

async function fetchAllMessagesForRange(
  startISO: string,
  endISO: string,
  credentials: ExportCredentials,
  filterParams?: Record<string, string>,
  statusFilter?: ExportStatusFilter
): Promise<CalendarMessage[]> {
  const { accessToken, userId, email, isSwitchedUser, username } = credentials;
  const auth = isSwitchedUser ? { username, logged_in_access_token: accessToken } : { access_token: accessToken };

  // The API only returns posts matching the given statuses — without it, it defaults to scheduled posts.
  // For sent/failed, inject the right default if the user doesn't already have a statuses filter active.
  const STATUS_DEFAULTS: Partial<Record<ExportStatusFilter, string>> = {
    sent: 'Sent',
    failed: 'Bounced',
  };
  const { statuses: existingStatuses, ...restFilterParams } = (filterParams ?? {}) as Record<string, string>;
  const statusDefault = statusFilter ? STATUS_DEFAULTS[statusFilter] : undefined;
  const resolvedStatuses = existingStatuses ?? statusDefault;
  const baseFilterParams = {
    ...restFilterParams,
    ...(resolvedStatuses ? { statuses: resolvedStatuses } : {}),
  };

  const sharedParams = {
    ...auth,
    username: email,
    dashboardId: userId,
    from: `${startISO} 00:00:00`,
    to: `${endISO} 23:59:59`,
    ...baseFilterParams,
  };

  const allMessages = await fetchPagedMessages(sharedParams);

  return Object.values(allMessages).sort((a, b) => extractSendDate(a).localeCompare(extractSendDate(b)));
}

function getRecurrenceLabelPlain(recurrence: CalendarRecurrence | undefined): string {
  if (!recurrence?.pattern) return '';

  const match = recurrence.pattern.match(/^(\d{2})([DHWMY])([01]{7})?$/);

  if (!match) return 'Repeating';

  const [, frequencyRaw, unit, maskRaw] = match;
  const frequency = Math.max(1, Number(frequencyRaw));
  const mask = maskRaw ?? '1111111';
  const unitNames: Record<string, [string, string]> = {
    D: ['day', 'days'],
    H: ['hour', 'hours'],
    W: ['week', 'weeks'],
    M: ['month', 'months'],
    Y: ['year', 'years'],
  };
  const [singular, plural] = unitNames[unit] ?? ['day', 'days'];
  const unitLabel = frequency === 1 ? `every ${singular}` : `every ${frequency} ${plural}`;

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const weekdays = mask
    .split('')
    .map((v, i) => (v === '1' ? dayNames[i] : null))
    .filter(Boolean) as string[];
  const allDays = weekdays.length === 7;
  let detail = unitLabel;

  if (!allDays && weekdays.length > 0) {
    const joined = weekdays.length === 1 ? weekdays[0] : `${weekdays.slice(0, -1).join(', ')} and ${weekdays[weekdays.length - 1]}`;

    detail = unit === 'D' && frequency === 1 ? `every ${joined}` : `${unitLabel} (${joined} only)`;
  }

  const untilValue = recurrence.untilLocalized ?? recurrence.until;

  if (!untilValue) return `Repeating ${detail}`;

  const untilDate = new Date(untilValue);

  if (Number.isNaN(untilDate.getTime())) return `Repeating ${detail}`;

  const formattedUntil = untilDate.toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' });

  return `Repeating ${detail} until ${formattedUntil}`;
}

let _exportSvgUid = 0;

function getChannelSvg(channelType: string): string {
  if (!channelType) return '';

  const uid = ++_exportSvgUid;
  const ct = channelType.toLowerCase();
  const svgOpen = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">`;
  const svgClose = `</svg>`;

  if (ct === 'facebook(page)') {
    return `${svgOpen}<circle cx="12" cy="12" r="11" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M23 12C23 5.92486 18.0751 1 12 1C5.92486 1 1 5.92486 1 12C1 17.4903 5.02252 22.0412 10.2813 22.8664V15.1797H7.48828V12H10.2813V9.57656C10.2813 6.81969 11.9235 5.29688 14.4361 5.29688C15.6392 5.29688 16.8984 5.51172 16.8984 5.51172V8.21875H15.5114C14.145 8.21875 13.7188 9.06674 13.7188 9.9375V12H16.7695L16.2818 15.1797H13.7188V22.8664C18.9775 22.0412 23 17.4903 23 12Z" fill="#1877F2"/>${svgClose}`;
  }

  if (ct === 'instagrampost' || ct === 'instagrambusiness') {
    const gid = `ig-${uid}`;

    return `${svgOpen}<defs><linearGradient id="${gid}" x1="8" x2="12" y1="23" y2="0.5" gradientUnits="userSpaceOnUse"><stop stop-color="#FAB153"/><stop offset="0.433" stop-color="#E3595C"/><stop offset="0.75" stop-color="#BF3496"/><stop offset="1" stop-color="#8047C0"/></linearGradient></defs><path fill="url(#${gid})" fill-rule="evenodd" clip-rule="evenodd" d="M18.802 5.196q.387.39.387.93 0 .545-.387.933a1.27 1.27 0 0 1-.93.387q-.544 0-.931-.387a1.27 1.27 0 0 1-.385-.932q-.002-.543.385-.93t.93-.388q.546 0 .93.387m-4.21 9.396A3.53 3.53 0 0 0 15.666 12a3.53 3.53 0 0 0-1.074-2.592A3.53 3.53 0 0 0 12 8.334a3.53 3.53 0 0 0-2.592 1.074A3.53 3.53 0 0 0 8.334 12q0 1.52 1.074 2.592A3.54 3.54 0 0 0 12 15.666a3.54 3.54 0 0 0 2.592-1.074m1.403-6.59q1.647 1.65 1.647 3.998-.001 2.349-1.646 3.998Q14.348 17.64 12 17.642q-2.35-.002-3.997-1.644Q6.356 14.348 6.356 12t1.647-3.998Q9.65 6.36 12 6.358q2.348.002 3.996 1.644m-2.9-5.032L12 2.976l-1.097-.006a87 87 0 0 0-1.51 0q-.516.006-1.384.043c-.575.023-1.07.072-1.474.143s-.748.16-1.024.265a3.7 3.7 0 0 0-1.26.832q-.543.544-.83 1.258a5.5 5.5 0 0 0-.265 1.024q-.108.61-.145 1.476a62 62 0 0 0-.041 1.382q-.01.515 0 1.512L2.976 12l-.006 1.095a58 58 0 0 0 0 1.51c.004.344.02.806.04 1.384q.038.867.146 1.474.105.61.265 1.026a3.76 3.76 0 0 0 2.09 2.09c.276.105.62.192 1.024.265q.61.109 1.474.143.869.037 1.384.043.516.007 1.51 0L12 21.022l1.095.008q.994.007 1.51 0c.347-.004.804-.02 1.384-.043.575-.023 1.07-.07 1.474-.143q.61-.108 1.024-.265a3.716 3.716 0 0 0 2.09-2.09q.16-.415.265-1.026c.075-.405.12-.897.146-1.474a51 51 0 0 0 .04-2.894L21.022 12l.006-1.095q.01-.997 0-1.512a51 51 0 0 0-.04-1.382c-.026-.577-.071-1.07-.146-1.476a5.5 5.5 0 0 0-.265-1.024 3.748 3.748 0 0 0-2.09-2.09 5.6 5.6 0 0 0-1.024-.265c-.405-.07-.899-.12-1.474-.143a52 52 0 0 0-1.384-.043 87 87 0 0 0-1.51 0M22.998 12q0 3.28-.07 4.54-.144 2.979-1.776 4.612t-4.611 1.775Q15.277 23 12 23 8.718 23 7.458 22.927q-2.98-.141-4.613-1.775-1.631-1.634-1.775-4.611Q1 15.28 1 12t.07-4.539q.146-2.98 1.776-4.613Q4.478 1.215 7.459 1.07C8.3 1.026 9.812 1 12 1q3.278.002 4.54.07 2.98.146 4.612 1.778t1.775 4.613q.07 1.26.071 4.539"/>${svgClose}`;
  }

  if (ct === 'twitteroauth') {
    return `${svgOpen}<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" fill="#000"/>${svgClose}`;
  }

  if (ct === 'linkedincompany' || ct === 'linkedinoauth') {
    return `${svgOpen}<path fill-rule="evenodd" clip-rule="evenodd" d="M23 5.1258V18.8742C23 20.0121 22.5979 20.9831 21.7894 21.7894C20.9831 22.5979 20.0121 23 18.8742 23H5.1258C3.99008 23 3.01692 22.5979 2.21058 21.7894C1.40424 20.9831 1 20.0121 1 18.8742V5.1258C1 3.98794 1.40424 3.01692 2.21058 2.21058C3.01692 1.4021 3.99008 1 5.1258 1H18.8742C20.0121 1 20.9831 1.4021 21.7894 2.21058C22.5979 3.01692 23 3.98794 23 5.1258Z" fill="#0077B5"/><path fill-rule="evenodd" clip-rule="evenodd" d="M16.2969 19.4175H19.6057V13.7175C19.6057 12.2482 19.2571 11.136 18.5598 10.3831C17.8625 9.62808 16.9407 9.24951 15.7964 9.24951C14.4982 9.24951 13.4993 9.80775 12.8042 10.9264H12.8299V9.48051H9.52111C9.55105 10.1093 9.55105 13.4245 9.52111 19.4175H12.8299V13.863C12.8299 13.4994 12.8641 13.232 12.9304 13.0609C13.0758 12.7251 13.2897 12.4428 13.5763 12.2075C13.8629 11.9744 14.2158 11.8567 14.635 11.8567C15.743 11.8567 16.2969 12.6075 16.2969 14.1047V19.4175Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M4.39433 19.4175H7.7031V9.48047H4.39433V19.4175Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.91699 6.4134C7.91057 5.91933 7.73518 5.50654 7.40153 5.18144C7.06787 4.85847 6.62513 4.69592 6.07118 4.69592C5.51508 4.69592 5.06379 4.85847 4.7173 5.18144C4.36867 5.50654 4.19328 5.91933 4.19328 6.4134C4.19328 6.90106 4.36225 7.30957 4.70446 7.63895C5.0424 7.96833 5.48514 8.13302 6.0284 8.13302H6.04123C6.60588 8.13302 7.05931 7.96833 7.40153 7.63895C7.74588 7.30957 7.91699 6.90106 7.91699 6.4134Z" fill="white"/>${svgClose}`;
  }

  if (ct === 'googlemybusinesslocation') {
    return `${svgOpen}<path d="M3.51879 6.80518H20.4361C21.5074 6.80518 22.3759 7.67368 22.3759 8.74503V20.5677C22.3759 21.6391 21.5074 22.5076 20.4361 22.5076H3.51879C2.44744 22.5076 1.57894 21.6391 1.57894 20.5677V8.74503C1.57894 7.67368 2.44744 6.80518 3.51879 6.80518Z" fill="#4788F8"/><path d="M6.00067 9.43434H12.0007V1.55945H6.89909L6.00067 9.43434Z" fill="#4A58BD"/><path d="M18.0007 9.43434H12.0007V1.55945H17.1023L18.0007 9.43434Z" fill="#7DABFB"/><path d="M22.2131 3.07883L22.2057 3.05199C22.0154 2.18093 21.2445 1.55976 20.3529 1.5592H17.1023L18 9.43432H24L22.2131 3.07883Z" fill="#4A58BD"/><path d="M1.78805 3.07883L1.79549 3.05199C1.98572 2.18093 2.75668 1.55976 3.64827 1.5592H6.8991L6 9.43432H0L1.78805 3.07883Z" fill="#7DABFB"/><path fill-rule="evenodd" clip-rule="evenodd" d="M6.01083 9.43237C6.01083 11.0883 4.66844 12.4307 3.01252 12.4307C1.3566 12.4307 0.014206 11.0883 0.0142059 9.43237H6.01083Z" fill="#7DA4E6"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.0074 9.43237C12.0074 11.0883 10.665 12.4307 9.00912 12.4307C7.3532 12.4307 6.01081 11.0883 6.01081 9.43237H12.0074Z" fill="#4855AF"/><path fill-rule="evenodd" clip-rule="evenodd" d="M18.0041 9.43237C18.0041 11.0883 16.6617 12.4307 15.0058 12.4307C13.3498 12.4307 12.0074 11.0883 12.0074 9.43237H18.0041Z" fill="#7DA4E6"/><path fill-rule="evenodd" clip-rule="evenodd" d="M24 9.43237C24 11.0883 22.6576 12.4307 21.0017 12.4307C19.3458 12.4307 18.0034 11.0883 18.0034 9.43237H24Z" fill="#4855AF"/><path d="M20.6185 17.557C20.6092 17.4314 20.5937 17.3168 20.5717 17.1705H17.6966C17.6966 17.5726 17.6966 18.0114 17.695 18.4138H19.3606C19.2891 18.7925 19.0697 19.1271 18.7509 19.3435C18.5458 19.4702 18.3161 19.5599 18.0755 19.5995C17.8342 19.6436 17.5869 19.6426 17.346 19.5963C17.1014 19.5457 16.8699 19.4454 16.6657 19.3015C16.3643 19.0878 16.1319 18.7908 15.9969 18.4469C15.8389 17.9762 15.8385 17.5591 15.9689 17.1743C16.0608 16.9043 16.2119 16.6584 16.4113 16.4545C16.879 15.9707 17.5778 15.7887 18.222 15.983C18.4691 16.0587 18.6948 16.1916 18.8809 16.3708L19.4429 15.8089C19.542 15.7087 19.6449 15.6118 19.7403 15.5083C19.4554 15.2425 19.1209 15.0354 18.756 14.8987C18.0919 14.6579 17.3654 14.6516 16.6972 14.8807C16.674 14.8886 16.6508 14.8969 16.6278 14.9055C15.9069 15.1765 15.3139 15.708 14.9658 16.395C14.8429 16.6382 14.7534 16.8968 14.6997 17.164C14.3814 18.7457 15.3466 20.3027 16.9048 20.7212C17.4142 20.8576 17.9557 20.8543 18.4691 20.7377C18.9355 20.6324 19.3668 20.4082 19.7211 20.0871C20.0907 19.747 20.3556 19.2939 20.4944 18.8127C20.6104 18.4049 20.6525 17.9796 20.6185 17.557Z" fill="#EEEEEF"/>${svgClose}`;
  }

  if (ct === 'youtube') {
    return `${svgOpen}<path fill="red" fill-rule="evenodd" clip-rule="evenodd" d="M20.784 7.35a2.3 2.3 0 0 0-1.621-1.631C17.733 5.333 12 5.333 12 5.333s-5.733 0-7.163.386a2.3 2.3 0 0 0-1.62 1.631c-.384 1.44-.384 4.442-.384 4.442s0 3.002.383 4.441a2.27 2.27 0 0 0 1.621 1.605c1.43.386 7.163.386 7.163.386s5.733 0 7.163-.386a2.27 2.27 0 0 0 1.62-1.605c.384-1.439.384-4.441.384-4.441s0-3.003-.383-4.442"/><path fill="#fff" fill-rule="evenodd" clip-rule="evenodd" d="M10.125 14.518V9.066l4.792 2.726z"/>${svgClose}`;
  }

  if (ct === 'pinterestoauth') {
    return `${svgOpen}<path fill-rule="evenodd" clip-rule="evenodd" d="M21.5263 6.47968C22.5081 8.16508 23 10.0045 23 12C23 13.9955 22.5081 15.8371 21.5263 17.5225C20.5425 19.2079 19.2079 20.5425 17.5225 21.5242C15.8371 22.5081 13.9955 23 12.0021 23C10.9413 23 9.89967 22.8481 8.87945 22.5402C9.43982 21.6525 9.81412 20.8697 9.99592 20.1939C10.0815 19.8666 10.3381 18.8614 10.768 17.1717C10.9584 17.5417 11.307 17.8647 11.8139 18.1384C12.3208 18.4101 12.8641 18.5448 13.4458 18.5448C14.6008 18.5448 15.6339 18.2197 16.5407 17.5631C17.4476 16.9086 18.1491 16.0103 18.6453 14.8639C19.1416 13.7175 19.3918 12.4299 19.3918 10.9969C19.3918 9.90823 19.1073 8.888 18.5384 7.93195C17.9716 6.97803 17.146 6.19949 16.0681 5.59634C14.9879 4.99533 13.771 4.6959 12.4149 4.6959C11.4118 4.6959 10.4772 4.83492 9.60665 5.11083C8.73829 5.38888 8.00253 5.75676 7.3951 6.21447C6.78981 6.67218 6.26794 7.19833 5.83589 7.7972C5.39957 8.39393 5.07875 9.01206 4.87556 9.65157C4.67023 10.2911 4.56757 10.9327 4.56757 11.5701C4.56757 12.5625 4.75792 13.4373 5.14077 14.1923C5.52149 14.9473 6.07972 15.4756 6.81548 15.7815C7.10208 15.8969 7.28388 15.8007 7.36088 15.4948C7.37799 15.4285 7.41649 15.281 7.47424 15.05C7.53199 14.8211 7.56835 14.68 7.58973 14.6201C7.64534 14.4019 7.59401 14.1966 7.43146 14.0041C6.94381 13.4245 6.69998 12.7015 6.69998 11.8417C6.69998 10.4023 7.20047 9.16177 8.19716 8.12658C9.19599 7.09139 10.5007 6.57165 12.1134 6.57165C13.5571 6.57165 14.6821 6.96306 15.4884 7.74587C16.2926 8.53082 16.699 9.54676 16.699 10.7958C16.699 12.4213 16.3718 13.7988 15.7173 14.9366C15.0628 16.0723 14.2265 16.6413 13.2042 16.6413C12.6224 16.6413 12.154 16.4338 11.8011 16.0167C11.446 15.6018 11.337 15.1034 11.4717 14.5195C11.5466 14.1859 11.6728 13.741 11.8503 13.1806C12.0257 12.6245 12.1711 12.1305 12.2802 11.707C12.3893 11.2835 12.4449 10.922 12.4449 10.6247C12.4449 10.1478 12.3144 9.75209 12.0577 9.43554C11.8011 9.12114 11.4311 8.965 10.9541 8.965C10.3617 8.965 9.86117 9.23663 9.45052 9.7799C9.03986 10.3232 8.83453 11.0033 8.83453 11.8139C8.83453 12.5112 8.95431 13.0929 9.19386 13.5613L7.77581 19.5479C7.61326 20.2152 7.55123 21.0622 7.58973 22.0824C5.62201 21.2141 4.03286 19.873 2.82014 18.0593C1.60529 16.2456 1 14.2265 1 12C1 10.0045 1.49407 8.16508 2.47579 6.47968C3.45752 4.79214 4.79428 3.45752 6.47968 2.47365C8.16508 1.49193 10.0045 1 12.0021 1C13.9955 1 15.8371 1.49193 17.5225 2.47365C19.2079 3.45752 20.5425 4.79214 21.5263 6.47968" fill="#BD081C"/>${svgClose}`;
  }

  if (ct === 'tiktokbusiness') {
    return `${svgOpen}<path fill-rule="evenodd" clip-rule="evenodd" d="M10.025 19.372a3.063 3.063 0 003.062-2.932V1.919h2.649A5.187 5.187 0 0115.629 1h-3.614v14.544a3.06 3.06 0 01-4.447 2.58 3.062 3.062 0 002.457 1.248zm-.16-9.707v-.857A6.668 6.668 0 005.15 20.903 6.66 6.66 0 019.863 9.665zm10.808-3.628v.811a5.037 5.037 0 01-2.74-1.623c.815.531 1.767.813 2.74.812z" fill="#25F4EE"/><path fill-rule="evenodd" clip-rule="evenodd" d="M17.925 5.225a5.021 5.021 0 01-1.24-3.306h-.965a5.027 5.027 0 002.205 3.306zM8.969 12.36A3.062 3.062 0 007.56 18.1a2.97 2.97 0 01-.581-1.79 3.062 3.062 0 013.061-3.062c.305.001.607.05.896.145V9.72a6.89 6.89 0 00-.896-.061h-.176v2.824a3.165 3.165 0 00-.895-.122zm11.704-2.695V6.848c.344.077.696.116 1.049.115v3.62a8.62 8.62 0 01-5.037-1.622v7.349a6.66 6.66 0 01-11.536 4.593 6.66 6.66 0 0010.48-5.497V8.042a8.635 8.635 0 005.044 1.623z" fill="#FE2C55"/><path d="M15.629 15.406V8.042a8.635 8.635 0 005.044 1.623V6.848a5.037 5.037 0 01-2.74-1.623 5.092 5.092 0 01-2.22-3.306h-2.649v14.544a3.062 3.062 0 01-5.504 1.66 3.062 3.062 0 011.409-5.74c.302-.013.604.02.895.1V9.664A6.66 6.66 0 005.15 20.903a6.659 6.659 0 0010.48-5.466v-.03z" fill="#000"/>${svgClose}`;
  }

  if (ct === 'threads') {
    return `${svgOpen}<path d="M17.218 11.197a8 8 0 0 0-.289-.131c-.17-3.13-1.88-4.92-4.75-4.939h-.039c-1.717 0-3.145.733-4.024 2.067l1.579 1.083c.656-.997 1.687-1.209 2.446-1.209h.026c.945.006 1.659.281 2.12.817.336.39.56.93.672 1.61a12 12 0 0 0-2.713-.13c-2.73.156-4.484 1.748-4.366 3.96.06 1.122.618 2.087 1.573 2.718.808.533 1.848.794 2.929.735 1.427-.079 2.547-.623 3.328-1.62.594-.755.969-1.735 1.135-2.97.68.41 1.184.95 1.463 1.6.473 1.104.5 2.918-.98 4.397-1.297 1.296-2.856 1.857-5.212 1.874-2.613-.02-4.59-.858-5.875-2.491-1.204-1.53-1.826-3.74-1.849-6.568.023-2.828.645-5.038 1.849-6.568 1.285-1.633 3.262-2.471 5.875-2.49 2.633.019 4.644.86 5.978 2.502.654.805 1.148 1.818 1.473 2.998l1.85-.493c-.394-1.453-1.015-2.705-1.859-3.744-1.71-2.104-4.212-3.183-7.435-3.205h-.013c-3.217.022-5.691 1.105-7.353 3.217-1.479 1.88-2.242 4.496-2.267 7.775v.016c.025 3.279.788 5.895 2.267 7.775C6.419 21.895 8.893 22.978 12.11 23h.013c2.86-.02 4.876-.769 6.536-2.428 2.173-2.17 2.108-4.892 1.392-6.562-.514-1.198-1.494-2.17-2.833-2.813m-4.938 4.642c-1.197.068-2.44-.47-2.5-1.62-.046-.852.606-1.803 2.573-1.917q.337-.02.664-.02c.714 0 1.382.07 1.99.203-.227 2.83-1.556 3.29-2.727 3.354" stroke-linecap="round" stroke-linejoin="round" fill="#000"/>${svgClose}`;
  }

  if (ct === 'bluesky') {
    return `${svgOpen}<path d="M5.769 3.423C8.29 5.317 11.004 9.156 12 11.217c.996-2.06 3.709-5.9 6.231-7.794C20.051 2.057 23 1 23 4.363c0 .673-.385 5.645-.611 6.452-.785 2.806-3.647 3.522-6.192 3.089 4.449.757 5.58 3.265 3.137 5.773-4.643 4.764-6.673-1.195-7.193-2.722-.096-.28-.14-.41-.141-.3 0-.11-.045.02-.14.3-.52 1.527-2.55 7.486-7.193 2.722-2.445-2.508-1.313-5.016 3.136-5.773-2.545.433-5.407-.283-6.192-3.09C1.385 10.009 1 5.037 1 4.365 1 1 3.949 2.057 5.769 3.424" stroke-linecap="round" stroke-linejoin="round" fill="#0085FF"/>${svgClose}`;
  }

  if (ct.includes('wordpress')) {
    return `${svgOpen}<path fill="#3858E9" d="M12 1C5.92 1 1 5.92 1 12s4.92 11 11 11 11-4.95 11-11c0-6.08-4.95-11-11-11M2.1 12c0-1.436.306-2.78.856-4.033L7.66 20.892C4.391 19.302 2.1 15.912 2.1 12m9.9 9.9c-.978 0-1.894-.153-2.78-.397l2.963-8.617 3.025 8.342c.03.06.03.091.061.122-1.008.336-2.108.55-3.269.55m1.375-14.544c.58-.031 1.13-.092 1.13-.092.52-.061.459-.856-.06-.825 0 0-1.59.122-2.628.122-.978 0-2.598-.122-2.598-.122-.519-.03-.61.794-.06.825 0 0 .519.061 1.038.092l1.528 4.216-2.17 6.509L5.95 7.356c.611-.031 1.13-.092 1.13-.092.52-.061.459-.856-.06-.825 0 0-1.59.122-2.628.122H3.75A9.82 9.82 0 0 1 12 2.1c2.567 0 4.92.978 6.692 2.597h-.123c-.977 0-1.65.856-1.65 1.742 0 .825.459 1.497.978 2.322.367.672.825 1.497.825 2.72 0 .855-.244 1.925-.764 3.208l-.977 3.3zm3.606 13.2 3.025-8.74c.55-1.405.763-2.535.763-3.544 0-.366-.03-.703-.06-1.008A9.9 9.9 0 0 1 21.93 12a10.02 10.02 0 0 1-4.95 8.556"/>${svgClose}`;
  }

  if (ct === 'group') {
    return `${svgOpen}<path fill="#000" d="M12 1c6.075 0 11 4.925 11 11s-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1M8 13a3 3 0 1 0 0 6 3 3 0 0 0 0-6m8 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6m8 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6"/>${svgClose}`;
  }

  return '';
}

function getAvatarInitials(name: string): string {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarTextColor(hex: string): string {
  try {
    const clean = hex.replace('#', '');
    const full =
      clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    return luminance > 0.6 ? '#1a1a2e' : '#ffffff';
  } catch {
    return '#ffffff';
  }
}

function formatExportTime(isoOrTime: string): string {
  const timeOnly = /^(\d{2}):(\d{2})$/.exec(isoOrTime);

  if (timeOnly) return isoOrTime.slice(0, 5);

  const tIdx = isoOrTime.indexOf('T');

  if (tIdx !== -1) return isoOrTime.slice(tIdx + 1, tIdx + 6);

  return isoOrTime.slice(11, 16);
}

function formatExportDayLabel(dayISO: string): { num: string; rest: string } {
  const [y, m, d] = dayISO.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return { num: String(d), rest: `${months[m - 1]}, ${days[dt.getDay()]}` };
}

function getIsWorkspaceMode(): boolean {
  const user = sharedStateBridge.getSharedState()?.user;

  return Boolean(user?.isWorkspaceMode);
}

function buildDailyListHtml(messages: CalendarMessage[], startISO: string, endISO: string, title: string, filterChipsHtml = ''): string {
  const byDay: Record<string, CalendarMessage[]> = {};

  messages.forEach((m) => {
    const day = (m.sendDateLocalized || extractSendDate(m)).slice(0, 10);

    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(m);
  });

  const daysHtml = Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, msgs]) => {
      const { num, rest } = formatExportDayLabel(day);

      const msgsHtml = msgs
        .map((m) => {
          const sendDate = m.sendDateLocalized || extractSendDate(m);
          const time = formatExportTime(sendDate);
          const text = renderLinksToHtml(processTags(extractMessage(m) || m.subject));

          const isWorkspaceMode = getIsWorkspaceMode();
          const workspaceData = isWorkspaceMode ? m.author || m.workspace : m.workspace;

          const workspaceName = workspaceData?.name ?? '';
          const workspaceColor = workspaceData?.color ?? '#ccc';
          const initials = getAvatarInitials(workspaceName);
          const avatarTextColor = getAvatarTextColor(workspaceColor);

          // Profiles row: avatar image + social icon badge (bottom-right) + profile name
          const destinations: CalendarDestination[] = m.destinations ?? [];

          // Failure reasons — per-recipient error messages (only present after hydration)
          const failedRecipients = (m.deliveryStatus?.recipients ?? []).filter((r) => r.status !== 'Sent' && r.errorMessage);

          const hasFailureReason = Boolean(m.deliveryStatus?.failureReason);
          const failureReasonMessageKey = getFailureReasonMessage(m.deliveryStatus?.failureReason ?? '');
          const failureReasonMessage = failureReasonMessageKey ? window.translate(failureReasonMessageKey) : '';

          // When there are failure reasons, only show the failing profiles.
          // If no destination name matches (e.g. the destination is a list/group), fall back to all destinations.
          const matchingDestinations =
            failedRecipients.length > 0 ? destinations.filter((d) => failedRecipients.some((r) => r.name === d.channelName)) : destinations;
          const visibleDestinations = matchingDestinations.length > 0 ? matchingDestinations : destinations;

          const profilesHtml =
            visibleDestinations.length > 0
              ? `<div class="profiles-row">${visibleDestinations
                  .map((d) => {
                    const icon = d?.channelType ? getChannelSvg(d.channelType) : '';
                    const name = escapeHtml(d?.channelName ?? '');

                    return `<span class="profile-item"><span class="profile-avatar-wrap">${icon}</span><span>${name}</span></span>`;
                  })
                  .join('')}</div>`
              : '';

          // Campaigns row: colored dot avatar + campaign name
          const campaigns: CalendarCampaign[] = (m.campaigns ?? []) as CalendarCampaign[];
          const campaignsHtml =
            campaigns.length > 0
              ? `<div class="campaigns-row">${campaigns
                  .map((c) => {
                    const cColor = escapeHtml(c.color ?? '#ccc');
                    const cName = escapeHtml(c.name ?? '');
                    const cTextColor = getAvatarTextColor(c.color ?? '#ccc');
                    const cInitials = getAvatarInitials(c.name ?? '');

                    return `<span class="campaign-item"><span class="campaign-avatar" style="background:${cColor};color:${cTextColor}">${cInitials}</span><span>${cName}</span></span>`;
                  })
                  .join('')}</div>`
              : '';

          // Recurrence row
          const recurrenceLabel = getRecurrenceLabelPlain(m.recurrence);
          const recurrenceHtml = recurrenceLabel
            ? `<div class="recurrence-row"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg><span>${escapeHtml(
                recurrenceLabel
              )}</span></div>`
            : '';

          // Media thumbnails — one per media item, each opens URL in new tab
          const medias: CalendarMedia[] = getCalendarCardMedias(m.body);
          const mediaRowHtml =
            medias.length > 0
              ? `<div class="media-row">${medias
                  .map((md) => {
                    if (!md.url) return '';

                    const isMdPdf = md.type === 'Document' || /\.pdf(\?|#|$)/i.test(md.url);
                    const isMdVideo = md.type === 'Video';
                    const isMdGif = md.type === 'Gif';
                    const href = escapeHtml(md.url);

                    if (isMdPdf) {
                      return `<a class="card-thumb card-thumb--pdf" href="${href}" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#888" stroke-width="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></a>`;
                    }

                    const thumbSrc = isMdVideo && md.thumbnailUrl ? md.thumbnailUrl : md.url;
                    let badge = '';

                    if (md.isLinkPreview) {
                      badge = `<span class="thumb-badge"><svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="white" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></span>`;
                    } else if (isMdVideo) {
                      badge = `<span class="thumb-badge"><svg viewBox="0 0 24 24" width="10" height="10" fill="white"><polygon points="5,3 19,12 5,21"/></svg></span>`;
                    } else if (isMdGif) {
                      badge = `<span class="thumb-badge thumb-badge--text">GIF</span>`;
                    }

                    return `<a class="card-thumb" href="${href}" target="_blank" rel="noreferrer"><img src="${escapeHtml(
                      thumbSrc
                    )}" alt="" loading="lazy"/>${badge}</a>`;
                  })
                  .filter(Boolean)
                  .join('')}</div>`
              : '';

          // Failure reasons — per-recipient error messages (only present after hydration)
          const failureReasonsHtml =
            failedRecipients.length > 0
              ? `<div class="failure-reasons">${failedRecipients
                  .map((r) => {
                    const rName = escapeHtml(r.name ?? '');
                    const rError = escapeHtml(r.errorMessage ?? '');

                    return `<div class="failure-reason-item"><span class="failure-reason-name">${rName}:</span><span class="failure-reason-msg">${rError}</span></div>`;
                  })
                  .join('')}</div>`
              : '';

          const otherFailureReasonsHtml = hasFailureReason
            ? `<div class="failure-reasons"><div class="failure-reason-item"><span class="failure-reason-msg">${escapeHtml(
                failureReasonMessage ?? 'abc'
              )}</span></div></div>`
            : '';

          return `
            <div class="card">
              <div class="card-info">
                <span class="card-time">${escapeHtml(time)}</span>
              </div>
              <div class="card-content">
                ${profilesHtml}
                <p class="card-text">${text}</p>
                ${recurrenceHtml}
                ${campaignsHtml}
                ${mediaRowHtml}
                ${otherFailureReasonsHtml}
                ${failureReasonsHtml}
              </div>
              <div class="card-footer">
                <span class="avatar" style="background:${escapeHtml(workspaceColor)};color:${avatarTextColor}">${escapeHtml(
                  initials
                )}<span class="avatar-tooltip">${escapeHtml(workspaceName)}</span></span>
              </div>
            </div>`;
        })
        .join('');

      return `
        <div class="day-row">
          <div class="day-label">
            <span class="day-num">${escapeHtml(num)}</span>
            <span class="day-rest">${escapeHtml(rest)}</span>
          </div>
          <div class="msg-list">${msgsHtml}</div>
        </div>`;
    })
    .join('');

  const styles = `
    *, *::before, *::after { box-sizing: border-box; }
    body { font-family: system-ui, -apple-system, sans-serif; margin: 0; padding: 2rem; color: #1a1a2e; background: #fff; }
    .export-header { margin-bottom: 1.5rem; }
    .export-header h1 { font-size: 1.5rem; font-weight: 600; margin: 0 0 0.25rem; }
    .export-header p { font-size: 1rem; color: #888; margin: 0; }
    .export-header .filter-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.6rem; }
    .export-header .filter-chip { display: inline-flex; align-items: center; gap: 0.3rem; background: #f0f2f5; border-radius: 0.75rem; padding: 0.2rem 0.6rem; font-size: 0.75rem; color: #1a1a2e; }
    .export-header .filter-chip b { font-weight: 600; }
    .day-row { display: flex; align-items: flex-start; border-bottom: 1px solid #999; padding: 1.5rem 0.5rem; gap: 0; }
    .day-label { flex-shrink: 0; width: 7rem; display: flex; flex-direction: column; align-items: flex-start; padding-top: 0.125rem; cursor: default; }
    .day-num { font-size: 1.5rem; font-weight: 500; color: #1a1a2e; line-height: 1; }
    .day-rest { font-size: 0.625rem; letter-spacing: 0.05em; color: #666; margin-top: 0.15rem; }
    .msg-list { flex: 1; display: flex; flex-direction: column }
    .card { display: flex; align-items: flex-start; gap: 0.75rem; background: #fff; padding: 1rem 0.5rem; border-bottom: 1px solid #f0f2f5; }
    .card:nth-last-child(1) { border-bottom: none; }
    .card-info { display: flex; align-items: center; gap: 0.35rem; flex-shrink: 0; min-width: 4.5rem; padding-top: 0.15rem; }
    .card-time { font-size: 0.875rem; color: #555; white-space: nowrap; }
    .card-content { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.5rem; }
    .profiles-row { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .profile-item { display: inline-flex; align-items: center; gap: 0.4rem; background: #f0f2f5; border-radius: 0.5rem; padding: 0.2rem 0.6rem 0.2rem 0.25rem; font-size: 0.875rem; color: #444; }
    .profile-avatar-wrap { position: relative; width: 1.5rem; height: 1.5rem; flex-shrink: 0; border-radius: 0.25rem; overflow: visible; display: inline-flex; align-items: center; justify-content: center; }
    .profile-avatar-wrap svg { width: 1.5rem; height: 1.5rem; border-radius: 0.25rem; object-fit: cover; display: block; }
    .profile-avatar-fallback { width: 1.5rem; height: 1.5rem; border-radius: 0.25rem; display: inline-flex; align-items: center; justify-content: center; font-size: 0.6rem; font-weight: 700; color: #555; }
    .profile-network-badge { position: absolute; bottom: -0.2rem; right: -0.2rem; background: #fff; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; padding: 0.1rem; line-height: 0; }
    .profile-network-badge svg { width: 0.65rem; height: 0.65rem; display: block; }
    .campaigns-row { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .campaign-item { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; color: #444; }
    .campaign-avatar { display: inline-flex; align-items: center; justify-content: center; width: 1.1rem; height: 1.1rem; border-radius: 50%; font-size: 0.5rem; font-weight: 700; flex-shrink: 0; }
    .recurrence-row { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.75rem; color: #4a90d9; }
    .recurrence-row svg { flex-shrink: 0; }
    .card-text { font-size: 1rem; margin: 0; white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; color: #1a1a2e; }
    .card-text a { word-break: break-all }
    .media-row { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.35rem; }
    .failure-reasons { display: flex; flex-direction: column; gap: 0.3rem; margin-top: 0.4rem; padding: 0.5rem 0.6rem; background: rgba(229, 10, 31, 0.05); border-left: 3px solid #ce091b; border-radius: 0 0.25rem 0.25rem 0; }
    .failure-reason-item { display: flex; align-items: flex-start; gap: 0.35rem; font-size: 0.75rem; line-height: 1.4; color: #ce091b; }
    .failure-reason-icon { flex-shrink: 0; margin-top: 0.1rem; }
    .failure-reason-name { font-weight: 600; white-space: nowrap; flex-shrink: 0; }
    .failure-reason-msg { color: #555; word-break: break-word; }
    .card-thumb { width: 3rem; height: 3rem; border-radius: 0.25rem; overflow: hidden; position: relative; background: #f0f2f5; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; flex-shrink: 0; }
    .card-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .card-thumb--pdf { background: #f5f5f5; }
    .thumb-badge { position: absolute; bottom: 2px; right: 2px; background: rgba(0,0,0,0.55); border-radius: 2px; display: inline-flex; align-items: center; justify-content: center; padding: 1px 2px; }
    .thumb-badge--text { font-size: 6px; color: white; font-weight: 700; }
    .avatar { user-select: none; cursor: default; display: inline-flex; align-items: center; justify-content: center; position: relative; width: 1.75rem; height: 1.75rem; border-radius: 50%; font-size: 0.675rem; font-weight: 600; flex-shrink: 0; }
    .avatar-tooltip { display: none; position: absolute; bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%); background: #1a1a2e; color: #fff; font-size: 0.7rem; padding: 0.25rem 0.5rem; border-radius: 0.25rem; white-space: nowrap; pointer-events: none; z-index: 10; }
    .avatar:hover .avatar-tooltip { display: block; }
    .card-footer { display: flex; flex-direction: row; align-items: flex-start; gap: 0.35rem; flex-shrink: 0; padding-top: 0.15rem; }
    .empty { padding: 2rem 0; color: #999; font-size: 1rem; }
    @media print {
      body { padding: 1rem !important; padding-top: 1rem !important; }
      .day-row { page-break-inside: avoid; }
      .notice-bar { display: none !important; }
      .toast-container { display: none !important; }
    }
    ._pendo-badge, [class*="_pendo"] { display: none !important; }
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body>
  <div class="export-header">
    <h1>${escapeHtml(title)}</h1>
    <p>${escapeHtml(startISO)} – ${escapeHtml(endISO)}</p>
    ${filterChipsHtml ? `<div class="filter-chips">${filterChipsHtml}</div>` : ''}
  </div>
  ${daysHtml || '<p class="empty">No messages found for this period.</p>'}
</body>
</html>`;
}

export async function getFilteredMessages(
  startISO: string,
  endISO: string,
  credentials: ExportCredentials,
  filterParams?: Record<string, string>,
  statusFilter?: ExportStatusFilter,
  queueId?: string
): Promise<CalendarMessage[]> {
  let messages = await fetchAllMessagesForRange(startISO, endISO, credentials, filterParams, statusFilter);

  if (statusFilter === 'scheduled') {
    messages = messages.filter((m) => SCHEDULED_STATUSES.has(m.status));
  } else if (statusFilter === 'sent') {
    messages = messages.filter((m) => SENT_STATUSES.has(m.status));
  }
  // For 'failed': the API already returns only Bounced posts (status=Bounced).
  // The API returns these with status 'Sent', so we skip client-side filtering.

  if (queueId) {
    messages = messages.filter((m) => String((m as any).queueId) === queueId);
  }

  return messages;
}

/**
 * Hydrates failure reasons for failed messages by fetching each message individually.
 * Uses a concurrency limit to avoid flooding the API.
 */
async function fetchFailureReasons(messages: CalendarMessage[], credentials: ExportCredentials): Promise<CalendarMessage[]> {
  const { accessToken, userId, isSwitchedUser, username } = credentials;
  const auth = isSwitchedUser ? { username, logged_in_access_token: accessToken } : { access_token: accessToken };
  const CONCURRENCY = 10;
  const result = [...messages];

  for (let i = 0; i < messages.length; i += CONCURRENCY) {
    const batch = messages.slice(i, i + CONCURRENCY);

    const details = await Promise.all(
      batch.map(async (m) => {
        try {
          const response = await fetchWithHeaders({
            method: 'GET',
            url: `${endpoints.GetCalendarMessage.endpoint}/${m.id}`,
            params: { ...auth, dashboardId: userId },
          });
          const payload = response as unknown;

          if (typeof payload === 'object' && payload !== null && 'result' in payload) {
            return (payload as { result?: CalendarMessage }).result ?? null;
          }

          return payload as CalendarMessage;
        } catch {
          return null;
        }
      })
    );

    details.forEach((detail, batchIdx: number) => {
      if (!detail) return;

      const globalIdx = i + batchIdx;
      const recipients: CalendarRecipient[] = detail.deliveryStatus?.recipients ?? [];

      result[globalIdx] = { ...result[globalIdx], deliveryStatus: { ...result[globalIdx].deliveryStatus, recipients } };
    });
  }

  return result;
}

export async function exportFilteredDateRangeCsv(
  startISO: string,
  endISO: string,
  credentials: ExportCredentials,
  filterParams?: Record<string, string>,
  statusFilter?: ExportStatusFilter,
  queueId?: string
) {
  const messages = await getFilteredMessages(startISO, endISO, credentials, filterParams, statusFilter, queueId);
  const filename = `calendar-${startISO}-to-${endISO}.csv`;

  downloadCsv(buildRows(messages), filename);
}

export async function exportFilteredMessagesHtml(
  startISO: string,
  endISO: string,
  title: string,
  credentials: ExportCredentials,
  filterParams?: Record<string, string>,
  statusFilter?: ExportStatusFilter,
  queueId?: string,
  filterChipsHtml = ''
) {
  let messages = await getFilteredMessages(startISO, endISO, credentials, filterParams, statusFilter, queueId);

  if (statusFilter === 'failed') {
    messages = await fetchFailureReasons(messages, credentials);
  }

  const html = buildDailyListHtml(messages, startISO, endISO, title, filterChipsHtml);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = `calendar-${startISO}-to-${endISO}.html`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getFirstVisibleDayISO(): CalendarMonthString {
  const container = document.querySelector('.daily-grid');

  if (container) {
    const containerRect = container.getBoundingClientRect();
    const rows = Array.from(container.querySelectorAll<HTMLElement>('[data-date]'));
    const firstVisible = rows.find((row) => {
      const rowRect = row.getBoundingClientRect();

      return rowRect.bottom > containerRect.top && rowRect.top < containerRect.bottom;
    });

    if (firstVisible) {
      return (firstVisible.getAttribute('data-date') ?? keyFromDate(new Date())) as CalendarMonthString;
    }
  }

  return keyFromDate(new Date()) as CalendarMonthString;
}

export function exportDailyCurrentViewHtml(title: string, filterChipsHtml = '', filterParams: Record<string, string> = {}) {
  const anchorISO = getFirstVisibleDayISO();

  const anchorDate = new Date(anchorISO);
  const startDate = new Date(anchorDate);

  startDate.setDate(anchorDate.getDate() - DAILY_VIEW_DATE_RANGE_DAYS);

  const endDate = new Date(anchorDate);

  endDate.setDate(anchorDate.getDate() + DAILY_VIEW_DATE_RANGE_DAYS);

  const startISO = keyFromDate(startDate);
  const endISO = keyFromDate(endDate);

  const allMessages = collectCachedDailyMessages(filterParams);

  const messages = Object.values(allMessages)
    .filter((m) => {
      const date = extractSendDate(m).slice(0, 10);

      return date >= startISO && date <= endISO;
    })
    .sort((a, b) => extractSendDate(a).localeCompare(extractSendDate(b)));

  const html = buildDailyListHtml(messages, startISO, endISO, title, filterChipsHtml);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = `calendar-${startISO}-to-${endISO}.html`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function printFilteredMessages(
  startISO: string,
  endISO: string,
  title: string,
  credentials: ExportCredentials,
  filterParams?: Record<string, string>,
  statusFilter?: ExportStatusFilter,
  queueId?: string,
  filterChipsHtml = ''
) {
  let messages = await getFilteredMessages(startISO, endISO, credentials, filterParams, statusFilter, queueId);

  if (statusFilter === 'failed') {
    messages = await fetchFailureReasons(messages, credentials);
  }

  const html = buildDailyListHtml(messages, startISO, endISO, title, filterChipsHtml);
  const printWindow = window.open('', '_blank', 'width=900,height=700');

  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
}

export function exportCalendarSectionHtml(
  containerSelector = '[data-calendar-root]',
  excludeSelectors: string[] = ['[data-export-hide]', '._pendo-badge'],
  filename = 'calendar.html'
) {
  const node = document.querySelector(containerSelector);

  if (!node) return;

  const clone = node.cloneNode(true) as HTMLElement;

  excludeSelectors.forEach((sel) => {
    clone.querySelectorAll(sel).forEach((el) => el.remove());
  });

  // Remove any script tags from the clone to prevent external scripts from re-injecting elements
  clone.querySelectorAll('script').forEach((el) => el.remove());

  const styledTags = Array.from(document.querySelectorAll('style[data-styled], style[data-styled-components], style[data-sc]'))
    .filter((tag) => !tag.id?.toLowerCase().includes('pendo') && !(tag as HTMLElement).dataset?.pendoId)
    .map((tag) => tag.outerHTML)
    .join('\n');

  let inlinedCss = '';

  try {
    const sheets = Array.from(document.styleSheets) as CSSStyleSheet[];

    sheets.forEach((sheet) => {
      let rules: CSSRuleList | null = null;

      try {
        rules = sheet.cssRules;
      } catch {
        // Skip cross-origin or blocked sheets
      }
      if (!rules) return;

      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        inlinedCss += `${rule.cssText}\n`;
      }
    });
  } catch {
    // Ignore CSSOM failures
  }

  const htmlClass = document.documentElement.className;
  const bodyClass = document.body.className;
  const rootVars = getComputedStyle(document.documentElement);
  const bodyVars = getComputedStyle(document.body);
  const cssVars: string[] = [];

  for (let i = 0; i < rootVars.length; i++) {
    const name = rootVars[i];

    if (name.startsWith('--')) {
      cssVars.push(`${name}: ${rootVars.getPropertyValue(name)};`);
    }
  }
  for (let i = 0; i < bodyVars.length; i++) {
    const name = bodyVars[i];

    if (name.startsWith('--')) {
      cssVars.push(`${name}: ${bodyVars.getPropertyValue(name)};`);
    }
  }

  const { title } = document;
  const fontLink = '';

  const extraStyles = `<style>
    ${excludeSelectors.join(',')} { display: none !important; }
    ._pendo-badge,
    ._pendo-badge *,
    [class*="_pendo"] { display: none !important; }
    :root { ${cssVars.join(' ')} }
    html, body {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
      margin: 0;
      padding: 0;
    }
    .calendar-daily-grid, .calendar-weekly-grid, .calendar-monthly-grid {
      height: auto !important;
      min-height: auto !important;
      overflow: visible !important;
    }
    .weekly-grid {
      min-height: 80svh !important;
    }
    .weekly-grid .week-day-column {
      height: 80svh !important;
    }
    .daily-grid .calendar-card-content-area p {
      word-break: break-word;
      -webkit-line-clamp: unset;
    }
    .calendar-card { overflow: visible; }
    .export-profile-list { display: none; position: absolute; background: #fff; border: 1px solid #e8eaed; border-radius: 0.5rem; box-shadow: 0 4px 16px rgba(0,0,0,0.12); min-width: 10rem; max-width: 16rem; z-index: 1000; padding: 0.35rem 0; top: calc(100% + 2px); right: 0; }
    .export-profile-list.open { display: block; }
    .export-profile-list ul { list-style: none; margin: 0; padding: 0; }
    .export-profile-list li { display: flex; align-items: center; gap: 0.25rem; padding: 0 0.75rem; font-size: 0.8rem; color: #444; }
  </style>`;

  const exportActivationScript = `<script>
    (function() {
      // Avatar title
      document.querySelectorAll('[data-export-workspace]').forEach(function(el) {
        el.setAttribute('title', el.getAttribute('data-export-workspace'));
      });

      // Profile popover
      document.querySelectorAll('[data-export-profiles]').forEach(function(el) {
        try {
          var profiles = JSON.parse(el.getAttribute('data-export-profiles'));
          if (!profiles.length) return;
          var wrap = el.closest('[id^="calendar-card-message-"]') || el.parentElement;
          wrap.style.position = 'relative';
          var list = document.createElement('div');
          list.className = 'export-profile-list';
          var ul = document.createElement('ul');
          profiles.forEach(function(p) {
            var li = document.createElement('li');
            li.textContent = p.name;
            ul.appendChild(li);
          });
          list.appendChild(ul);
          wrap.appendChild(list);
          el.style.cursor = 'pointer';
          el.addEventListener('click', function(e) {
            e.stopPropagation();
            var isOpen = list.classList.contains('open');
            document.querySelectorAll('.export-profile-list.open').forEach(function(p) { p.classList.remove('open'); });
            if (!isOpen) list.classList.add('open');
          });
        } catch(e) {}
      });
      document.addEventListener('click', function() {
        document.querySelectorAll('.export-profile-list.open').forEach(function(p) { p.classList.remove('open'); });
      });
    })();
  ${'</'}script>`;

  const cssBundle = `<style>${inlinedCss}</style>`;

  const html = `<!DOCTYPE html>
  <html lang="en" class="${htmlClass}">
  <head>
    <meta charset="UTF-8" />
    <title>${escapeHtml(title)}</title>
    ${fontLink}
    ${styledTags}
    ${cssBundle}
    ${extraStyles}
  </head>
  <body class="${bodyClass}" style="padding:0 !important; padding-top:0 !important;">
    <div id="exported-calendar">
      ${clone.outerHTML}
    </div>
    ${exportActivationScript}
  </body>
  </html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
